import React, { useState, useEffect, useRef } from 'react';
import '../../styles/whatsappButton.css';
import Gigi from '../../img/gigiassistente/gigimenor.png'; // Imagem do assistente virtual

interface Message {
  from: 'bot' | 'user';
  text: string;
  isLink?: boolean;
  url?: string;
}

interface Specialidade {
  title: string;
  route: string;
  keywords: string[]; // sinônimos/palavras-chave
}

// Util para comparar textos ignorando acentos/maiúsculas
const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const especialidades: Specialidade[] = [
  {
    title: 'Psicologia TCC',
    route: '/especialidade/psicologiatcc',
    keywords: [
      'psico',
      'psicologo',
      'psicóloga',
      'psicologia',
      'tcc',
      'terapia cognitiva',
      'terapia tcc',
    ],
  },
  {
    title: 'Psicologia Sistêmica',
    route: '/especialidade/psicoSistemica',
    keywords: [
      'psico',
      'psicologia sistemica',
      'sistemica',
      'terapia sistemica',
      'constelacao',
    ],
  },
  {
    title: 'Psicologia Humanista',
    route: '/especialidade/psicoHumanista',
    keywords: [
      'psico',
      'psicologia humanista',
      'humanista',
      'abordagem centrada',
      'rogers',
    ],
  },
  {
    title: 'Fonoaudiologia',
    route: '/especialidade/fonoaudiologia',
    keywords: ['fono', 'fonoaudiologia', 'fonoaudiologo', 'fonoaudiologa'],
  },
  {
    title: 'Nutrição',
    route: '/especialidade/nutricao',
    keywords: ['nutri', 'nutricao', 'nutricionista', 'nutricion'],
  },
  {
    title: 'Fisioterapia',
    route: '/especialidade/fisioterapia',
    keywords: ['fisio', 'fisioterapia', 'fisioterapeuta'],
  },
  {
    title: 'Psicopedagogia',
    route: '/especialidade/psicopedagogia',
    keywords: ['psicoped', 'psicopedagogia', 'psicopedagogo', 'psicopedagoga'],
  },
  {
    title: 'Neurologia Pediátrica',
    route: '/especialidade/neurologistaPediatra',
    keywords: [
      'neuro pediatra',
      'neuropediatra',
      'neurologista pediatra',
      'neurologia pediatrica',
      'neuro infantil',
    ],
  },
  {
    title: 'Neurologia Adultos',
    route: '/especialidade/neurologistaAdultos',
    keywords: [
      'neurologista adulto',
      'neurologia adultos',
      'neuro adulto',
      'neuro adultos',
    ],
  },
  {
    title: 'Psiquiatria',
    route: '/especialidade/psiquiatria',
    keywords: ['psiquiatra', 'psiquiatria'],
  },
  {
    title: 'Terapeuta de Casais',
    route: '/especialidade/terapeutaCasais',
    keywords: ['terapia de casais', 'terapia casal', 'terapeuta casal', 'casais'],
  },
  {
    title: 'Pedagogia',
    route: '/especialidade/pedagogia',
    keywords: ['pedagogia', 'pedagogo', 'pedagoga'],
  },
];

const VirtualAssistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    { from: 'bot', text: 'Olá! Sou a Gigi, assistente virtual da Clínica Mediação 😊' },
    { from: 'bot', text: 'Para começar, qual o seu nome?' },
  ]);
  const [userInput, setUserInput] = useState('');
  const [step, setStep] = useState(1);
  const [userName, setUserName] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [showFeedbackOptions, setShowFeedbackOptions] = useState(false);
  const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
  const endOfMessagesRef = useRef<HTMLDivElement | null>(null);

  // Inicia/renova timer de inatividade
  const startInactivityTimer = () => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    inactivityTimer.current = setTimeout(() => {
      setMessages([
        { from: 'bot', text: 'Parece que você se afastou... Vamos começar de novo? 😊' },
        { from: 'bot', text: 'Qual o seu nome?' },
      ]);
      setStep(1);
      setUserName('');
    }, 150000);
  };

  useEffect(() => {
    if (showChat && !showFeedbackOptions) {
      startInactivityTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    if (endOfMessagesRef.current) {
      endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  const resetConversation = () => {
    setMessages([
      { from: 'bot', text: 'Olá! Sou a Gigi, assistente virtual da Clínica Mediação 😊' },
      { from: 'bot', text: 'Para começar, qual o seu nome?' },
    ]);
    setStep(1);
    setUserName('');
    setAwaitingConfirmation(false);
    setRetryCount(0);
  };

  const handleSend = () => {
    if (!userInput.trim()) return;

    const input = userInput.trim();
    const normalizedInput = normalize(input);
    const userMessage: Message = { from: 'user', text: input };
    setMessages((prev) => [...prev, userMessage]);
    setUserInput('');
    setIsTyping(true);

    setTimeout(() => {
      const newMessages: Message[] = [];

      if (step === 1) {
        setUserName(input);
        newMessages.push({ from: 'bot', text: `Prazer, ${input}! Em que posso ajudar?` });
        setStep(2);
      } else if (step === 2) {
        // Tratamento de confirmação "sim" ou "não"
        if (awaitingConfirmation) {
          const ans = normalizedInput;
          if (ans === 'sim') {
            newMessages.push({ from: 'bot', text: 'Fico feliz em ajudar! 😊' });
            newMessages.push({ from: 'bot', text: 'Antes de encerrar, poderia responder nossa pesquisa rapidinho?' });
            newMessages.push({ from: 'bot', text: 'Como você avalia minha ajuda hoje?' });
            setShowFeedbackOptions(true);
            setAwaitingConfirmation(false);
            setRetryCount(0);
          } else if (ans === 'nao') {
            if (retryCount >= 1) {
              newMessages.push({ from: 'bot', text: 'Sem problemas, vou te encaminhar para um atendimento humanizado agora mesmo.' });
              newMessages.push({
                from: 'bot',
                isLink: true,
                text: '👉 Falar com um atendente no WhatsApp',
                url: `https://wa.me/5551999339942?text=${encodeURIComponent(
                  `Olá me chamo ${userName}, não encontrei o que buscava e gostaria de ajuda de um atendente.`
                )}`,
              });
              setAwaitingConfirmation(false);
              setRetryCount(0);
            } else {
              newMessages.push({ from: 'bot', text: 'Certo, tente me dizer novamente o que está procurando 😊' });
              setRetryCount((prev) => prev + 1);
              setAwaitingConfirmation(false);
            }
          } else {
            newMessages.push({ from: 'bot', text: 'Por favor, responda apenas "sim" ou "não".' });
          }

          setMessages((prev) => [...prev, ...newMessages]);
          setIsTyping(false);
          return;
        }

        // ==== RECONHECIMENTO COM SINÔNIMOS ====
        const matches = especialidades.filter((s) =>
          s.keywords.some((k) => normalizedInput.includes(normalize(k)))
        );

        if (matches.length === 1) {
          const m = matches[0];
          newMessages.push({ from: 'bot', text: `Encontrei essa especialidade:` });
          newMessages.push({ from: 'bot', isLink: true, text: m.title, url: m.route });
          newMessages.push({ from: 'bot', text: 'Era isso que você buscava? (Responda "sim" ou "não")' });
          setAwaitingConfirmation(true);
        } else if (matches.length > 1) {
          newMessages.push({ from: 'bot', text: 'Encontrei algumas opções relacionadas. Clique para acessar:' });
          matches.forEach((m) => {
            newMessages.push({ from: 'bot', isLink: true, text: `• ${m.title}`, url: m.route });
          });
          newMessages.push({ from: 'bot', text: 'Se preferir, pode especificar melhor (ex.: "psico tcc", "psico sistêmica").' });
          // aqui não pedimos sim/não para evitar ambiguidade
        }

        // Agendamento
        else if (normalizedInput.includes('agendar') || normalizedInput.includes('atendimento')) {
          newMessages.push({ from: 'bot', text: `Certo ${userName}, para agendarmos seu atendimento clique no link abaixo:` });
          newMessages.push({
            from: 'bot',
            isLink: true,
            text: '👉 Enviar mensagem no WhatsApp',
            url: `https://wa.me/5551999339942?text=${encodeURIComponent(
              `Olá me chamo ${userName}, gostaria de agendar um atendimento com a Clínica Mediação.`
            )}`,
          });
          newMessages.push({ from: 'bot', text: 'Posso te ajudar com mais alguma coisa?' });
        }

        // Encaminhar para humano
        else if (
          normalizedInput.includes('atendente') ||
          normalizedInput.includes('humano') ||
          normalizedInput.includes('falar com') ||
          normalizedInput.includes('pessoa') ||
          normalizedInput.includes('quero falar') ||
          normalizedInput.includes('atendimento humano') ||
          normalizedInput.includes('suporte') ||
          normalizedInput.includes('ajuda') ||
          normalizedInput.includes('assistencia') ||
          normalizedInput.includes('apoio')
        ) {
          const initialMessages: Message[] = [
            { from: 'bot', text: `Claro ${userName}, vou te encaminhar para um atendimento humanizado agora mesmo!` },
            {
              from: 'bot',
              isLink: true,
              text: '👉 Falar com um atendente no WhatsApp',
              url: `https://wa.me/5551999339942?text=${encodeURIComponent(
                `Olá me chamo ${userName}, gostaria de falar com um atendente da Clínica Mediação.`
              )}`,
            },
          ];

          setMessages((prev) => [...prev, ...initialMessages]);
          setIsTyping(true); // exibe "digitando..."

          setTimeout(() => {
            setIsTyping(false);
            setMessages((prev) => [
              ...prev,
              { from: 'bot', text: 'Antes de encerrar, poderia responder rapidinho nossa pesquisa?' },
              { from: 'bot', text: 'Como você avalia minha ajuda hoje?' },
            ]);
            setShowFeedbackOptions(true);
          }, 4000);
        }

        // Livros
        else if (normalizedInput.includes('livro') || normalizedInput.includes('livros')) {
          newMessages.push({ from: 'bot', text: 'Você pode comprar nossos livros pelos links abaixo:' });
          newMessages.push({ from: 'bot', isLink: true, text: '📖 Ver Livros', url: '/livros' });
          newMessages.push({ from: 'bot', isLink: true, text: '📚 Comprar na Amazon', url: 'https://www.amazon.com.br' });
          newMessages.push({ from: 'bot', isLink: true, text: '📘 Comprar na Editora WAK', url: 'https://wakeditora.com.br' });
          newMessages.push({ from: 'bot', text: 'Se precisar de mais algo, estou à disposição!' });
        }

        // Palestras
        else if (normalizedInput.includes('palestra') || normalizedInput.includes('palestras')) {
          newMessages.push({ from: 'bot', text: 'Você pode conferir nossas palestras aqui:' });
          newMessages.push({ from: 'bot', isLink: true, text: '🎤 Ver Palestras', url: '/palestras' });
        }

        // Sobre
        else if (normalizedInput.includes('sobre') || normalizedInput.includes('quem somos')) {
          newMessages.push({ from: 'bot', text: 'Saiba mais sobre a Clínica Mediação clicando abaixo:' });
          newMessages.push({ from: 'bot', isLink: true, text: '🏥 Sobre a Clínica', url: '/sobre' });
        }

        else {
          newMessages.push({
            from: 'bot',
            text: 'Desculpe, não entendi exatamente. Você pode reformular ou escolher uma opção? Dicas: "nutri", "psico", "fisio", "fono", "livros", "clínica"...'
          });
        }
      }

      setMessages((prev) => [...prev, ...newMessages]);
      setIsTyping(false);
    }, 1800);
  };

  const handleToggle = () => {
    setShowChat((prev) => !prev);
    if (!showChat) {
      resetConversation();
    }
  };

  return (
    <>
      <div className="gigi-button" onClick={handleToggle}>
        <img src={Gigi} alt="Assistente Gigi" />
      </div>

      {showChat && (
        <div className="chat-container">
          <div className="chat-header">
            <div className="gigi-header-info">
              <img src={Gigi} alt="Gigi Assistente Virtual" className="gigi-header-avatar" />
              <span>Gigi - Assistente Virtual (V1.0 BETA)</span>
            </div>
            <button className="close-chat-button" onClick={handleToggle}>
              ×
            </button>
          </div>

          <div className="chat-body">
            {messages.map((msg, index) => (
              <div key={index} className={`chat-message ${msg.from}`}>
                {msg.isLink ? (
                  <a
                    href={msg.url}
                    target={msg.url?.startsWith('http') ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                    className="chat-link"
                  >
                    {msg.text}
                  </a>
                ) : (
                  <p>{msg.text}</p>
                )}
              </div>
            ))}

            {isTyping && <div className="chat-message bot typing">Gigi está digitando...</div>}

            {showFeedbackOptions && (
              <div className="feedback-options">
                {[1, 2, 3, 4].map((score) => {
                  const feedbackTexts = ['Ruim', 'Regular', 'Neutro', 'Bom'];
                  const emojis = ['😠', '😐', '😊', '😍'];
                  const bgColors = ['#f44336', '#fdd835', '#aed581', '#81c784'];

                  return (
                    <div className="feedback-item" key={score}>
                      <div className="feedback-label">{feedbackTexts[score - 1]}</div>
                      <button
                        className="feedback-button"
                        style={{ backgroundColor: bgColors[score - 1] }}
                        onClick={() => {
                          setMessages((prev) => [
                            ...prev,
                            { from: 'user', text: `${score} - ${feedbackTexts[score - 1]}` },
                            { from: 'bot', text: 'Obrigado pelo seu feedback! 😊' },
                          ]);
                          setShowFeedbackOptions(false);

                          // Espera 5 segundos e reinicia a conversa
                          setTimeout(() => {
                            resetConversation();
                          }, 5000);
                        }}
                      >
                        <div className="feedback-icon">{emojis[score - 1]}</div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div ref={endOfMessagesRef} />
          </div>

          <div className="chat-input">
            <input
              type="text"
              value={userInput}
              placeholder="Digite aqui..."
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            />
            <button onClick={handleSend}>Enviar</button>
          </div>
        </div>
      )}
    </>
  );
};

export default VirtualAssistant;
